import React, { useState } from 'react';
import { Card, CardHeader, CardBody } from '../../../components/card/card.jsx';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.jsx';
import cronService from '../../../services/cronService.js';
import toastService from '../../../services/toastService.js';

/**
 * Component for displaying and managing cron tasks
 */
const CronTaskList = ({ 
  tasks = [], 
  onEdit, 
  onDelete, 
  onToggle, 
  onTrigger,
  onViewExecutions,
  isLoading = false,
  userRole = 'user'
}) => {
  const [actionLoading, setActionLoading] = useState({});
  
  // Custom confirm dialog
  const { showConfirm, dialogState } = useConfirmDialog();
  
  // Handle task actions with loading states
  const handleAction = async (taskId, action, actionFn) => {
    setActionLoading(prev => ({ ...prev, [`${taskId}-${action}`]: true }));
    try {
      await actionFn(taskId);
    } catch (error) {
      console.error(`Error ${action} task:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${taskId}-${action}`]: false }));
    }
  };
  
  // Handle delete with confirmation
  const handleDeleteWithConfirm = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const confirmed = await showConfirm({
      title: 'Delete Cron Task',
      message: `Are you sure you want to delete the task "${task?.name || 'Unknown'}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger'
    });
    
    if (confirmed) {
      await handleAction(taskId, 'delete', onDelete);
    }
  };
  
  // Handle trigger with confirmation
  const handleTriggerWithConfirm = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const confirmed = await showConfirm({
      title: 'Trigger Task Now',
      message: `Are you sure you want to run the task "${task?.name || 'Unknown'}" immediately?`,
      confirmText: 'Run Now',
      cancelText: 'Cancel',
      confirmVariant: 'primary'
    });
    
    if (confirmed) {
      await handleAction(taskId, 'trigger', onTrigger);
    }
  };
  
  // Format next run time
  const formatNextRun = (nextRun) => {
    if (!nextRun) return 'Not scheduled';
    
    try {
      const date = new Date(nextRun);
      const now = new Date();
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      const diffMs = date.getTime() - now.getTime();
      
      // Format the actual date/time
      const dateStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
      
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      const fullDateTime = `${dateStr} at ${timeStr}`;
      
      if (diffMs < 0) {
        return `Overdue (was ${fullDateTime})`;
      }
      
      // Calculate relative time
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      let relativeTime;
      if (diffDays > 7) {
        relativeTime = fullDateTime;
      } else if (diffDays > 0) {
        relativeTime = `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
      } else if (diffHours > 0) {
        relativeTime = `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
      } else if (diffMinutes > 0) {
        relativeTime = `in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
      } else {
        relativeTime = 'very soon';
      }
      
      // Show relative time for near future, full date for far future
      if (diffDays <= 7) {
        return `${relativeTime} (${fullDateTime})`;
      } else {
        return fullDateTime;
      }
      
    } catch (error) {
      console.error('Error formatting next run date:', error);
      return 'Invalid date';
    }
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    const statusColors = cronService.getStatusColor(status);
    return `badge bg-${statusColors.bg} text-${statusColors.text}`;
  };
  
  // Get task status based on enabled state and last execution
  const getTaskStatus = (task) => {
    if (!task.isEnabled) return 'disabled';
    if (!task.lastExecution) return 'pending';
    
    const lastStatus = task.lastExecution.status;
    if (lastStatus === 'completed') return 'active';
    if (lastStatus === 'failed') return 'error';
    if (lastStatus === 'running') return 'running';
    
    return 'pending';
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2">Loading cron tasks...</div>
          </div>
        </CardBody>
      </Card>
    );
  }
  
  if (tasks.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-4">
            <i className="bi bi-clock-history display-4 text-muted mb-3"></i>
            <h5 className="text-muted">No Cron Tasks Found</h5>
            <p className="text-muted mb-0">
              Create your first automated security scan to get started.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }
  
  return (
    <>
      <div className="cron-task-list">
        {tasks.map((task) => {
          const taskStatus = getTaskStatus(task);
          const isActionLoading = (action) => actionLoading[`${task.id}-${action}`];
          
          return (
            <Card key={task.id} className="mb-3">
              <CardBody>
                <div className="row align-items-center">
                  {/* Task Info */}
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <h5 className="mb-0 me-3">{task.name}</h5>
                      <span className={getStatusBadgeClass(taskStatus)}>
                        {taskStatus.charAt(0).toUpperCase() + taskStatus.slice(1)}
                      </span>
                      {!task.isEnabled && (
                        <span className="badge bg-secondary ms-2">Disabled</span>
                      )}
                    </div>
                    
                    <div className="text-muted mb-2">
                      <i className="bi bi-link-45deg me-1"></i>
                      <span className="me-3">{task.targetUrl}</span>
                      
                      <i className="bi bi-clock me-1"></i>
                      <span className="me-3">{cronService.describeCronExpression(task.cronExpression)}</span>
                      
                      <i className="bi bi-tools me-1"></i>
                      <span>{task.selectedTools?.length || 0} tools</span>
                    </div>
                    
                    {task.description && (
                      <div className="text-muted small mb-2">
                        {task.description}
                      </div>
                    )}
                    
                    <div className="small text-muted">
                      <span className="me-3">
                        <strong>Next run:</strong> {formatNextRun(task.nextRun)}
                      </span>
                      {task.lastExecution && (
                        <span>
                          <strong>Last run:</strong> {cronService.formatDate(task.lastExecution.startTime)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Task Stats */}
                  <div className="col-md-3">
                    <div className="row text-center">
                      <div className="col-4">
                        <div className="fw-bold text-primary">{task.executionCount || 0}</div>
                        <div className="small text-muted">Runs</div>
                      </div>
                      <div className="col-4">
                        <div className="fw-bold text-success">{task.successCount || 0}</div>
                        <div className="small text-muted">Success</div>
                      </div>
                      <div className="col-4">
                        <div className="fw-bold text-danger">{task.failureCount || 0}</div>
                        <div className="small text-muted">Failed</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="col-md-3">
                    <div className="d-flex justify-content-end gap-1 flex-wrap">
                      {/* Toggle Enable/Disable */}
                      <button
                        className={`btn btn-sm ${
                          task.isEnabled ? 'btn-outline-warning' : 'btn-outline-success'
                        }`}
                        onClick={() => handleAction(task.id, 'toggle', onToggle)}
                        disabled={isActionLoading('toggle')}
                        title={task.isEnabled ? 'Disable task' : 'Enable task'}
                      >
                        {isActionLoading('toggle') ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className={`bi bi-${task.isEnabled ? 'pause' : 'play'}-fill`}></i>
                        )}
                      </button>
                      
                      {/* Trigger Now */}
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleTriggerWithConfirm(task.id)}
                        disabled={isActionLoading('trigger') || taskStatus === 'running'}
                        title="Run now"
                      >
                        {isActionLoading('trigger') ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-play-circle"></i>
                        )}
                      </button>
                      
                      {/* View Executions */}
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => onViewExecutions(task.id)}
                        title="View execution history"
                      >
                        <i className="bi bi-list-ul"></i>
                      </button>
                      
                      {/* Edit */}
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => onEdit(task)}
                        title="Edit task"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      
                      {/* Delete */}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteWithConfirm(task.id)}
                        disabled={isActionLoading('delete')}
                        title="Delete task"
                      >
                        {isActionLoading('delete') ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-trash"></i>
                        )}
                      </button>
                    </div>
                    
                    {/* Last execution status */}
                    {task.lastExecution && (
                      <div className="mt-2 text-end">
                        <small className="text-muted">
                          Last: 
                          <span className={`ms-1 ${cronService.getStatusColor(task.lastExecution.status).text}`}>
                            {task.lastExecution.status}
                          </span>
                          {task.lastExecution.duration && (
                            <span className="ms-1">({task.lastExecution.duration}s)</span>
                          )}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Expanded details for running tasks */}
                {taskStatus === 'running' && task.lastExecution && (
                  <div className="mt-3 pt-3 border-top">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="d-flex align-items-center">
                          <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                          <span className="fw-bold text-primary">Currently Running</span>
                          <span className="ms-2 text-muted">
                            Started: {cronService.formatDate(task.lastExecution.startTime)}
                          </span>
                        </div>
                        {task.lastExecution.progress && (
                          <div className="mt-2">
                            <div className="progress" style={{ height: '6px' }}>
                              <div 
                                className="progress-bar" 
                                style={{ width: `${task.lastExecution.progress}%` }}
                              ></div>
                            </div>
                            <small className="text-muted">
                              Progress: {task.lastExecution.progress}%
                            </small>
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 text-end">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleAction(task.id, 'stop', (id) => {
                            // This would need to be implemented in the parent component
                            toastService.info('Stop functionality not yet implemented');
                          })}
                          title="Stop execution"
                        >
                          <i className="bi bi-stop-circle"></i>
                          Stop
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          );
        })}
      </div>
      
      {/* Custom Confirm Dialog */}
      <ConfirmDialog
        isVisible={dialogState.isVisible}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        confirmVariant={dialogState.confirmVariant}
        size={dialogState.size}
        onConfirm={dialogState.onConfirm}
        onCancel={dialogState.onCancel}
      />
    </>
  );
};

export default CronTaskList;